import { useCallback, useMemo } from 'react';
import { effectivePinnedShortcuts, type ShortcutId, type ShortcutPersona } from '@evi/shared';
import { useAuthStore } from '@/store/auth';
import { updatePreferences } from '@/lib/api';
import { SHORTCUT_CATALOG, type ShortcutDef } from '@/lib/shortcuts';

export interface PinnedShortcut extends ShortcutDef {
  /** Resolved destination for THIS driver (home charger id folded in). */
  href: string;
}

/**
 * The driver's three home-screen pins, resolved.
 *
 * Reads the persisted auth-store user (so the first paint after a cold
 * start already has the right three — the store is rehydrated from
 * localStorage before /auth/me answers) and the live one thereafter:
 * `pinned_shortcuts` (the driver's own choice, null = defaults),
 * `shortcut_persona` (server-derived: homeowner / fleet / driver) and
 * `home_charger_id` (for the two pins that deep-link to it).
 *
 * `setPins` writes through PATCH /me/preferences and patches the store
 * optimistically, so every surface that renders the pins (map FAB, Menu
 * row) flips together; `resetPins` returns to the persona defaults.
 */
export function usePinnedShortcuts(): {
  persona: ShortcutPersona;
  pins: PinnedShortcut[];
  pinnedIds: readonly ShortcutId[];
  isCustom: boolean;
  homeChargerId: string | null;
  setPins: (ids: readonly ShortcutId[]) => Promise<void>;
  resetPins: () => Promise<void>;
} {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const persona: ShortcutPersona = user?.shortcut_persona ?? (user?.fleet_id ? 'fleet' : 'driver');
  const homeChargerId = user?.home_charger_id ?? null;
  const pinnedIds = useMemo(
    () => effectivePinnedShortcuts(user?.pinned_shortcuts, persona),
    [user?.pinned_shortcuts, persona],
  );
  const pins = useMemo<PinnedShortcut[]>(
    () => pinnedIds.map((id) => ({ ...SHORTCUT_CATALOG[id], href: SHORTCUT_CATALOG[id].to({ homeChargerId, persona }) })),
    [pinnedIds, homeChargerId, persona],
  );

  // Optimistic store patch, rolled back if the write fails — the store
  // is persisted to localStorage, so an un-rolled-back miss would show
  // pins the server never held until the next reconcile.
  const write = useCallback(async (next: string[] | null) => {
    const prev = useAuthStore.getState().user?.pinned_shortcuts ?? null;
    updateUser({ pinned_shortcuts: next });
    try {
      await updatePreferences({ pinned_shortcuts: next });
    } catch (err) {
      updateUser({ pinned_shortcuts: prev });
      throw err;
    }
  }, [updateUser]);

  const setPins = useCallback((ids: readonly ShortcutId[]) => write([...ids]), [write]);
  const resetPins = useCallback(() => write(null), [write]);

  return {
    persona,
    pins,
    pinnedIds,
    isCustom: Array.isArray(user?.pinned_shortcuts) && user.pinned_shortcuts.length > 0,
    homeChargerId,
    setPins,
    resetPins,
  };
}
